import {
  CARGO_TYPES,
  CONTAINER_SIZE,
  DISTANCE_UNITS,
  MODE_AIR,
  MODE_INTERMODAL,
  UNIT_WEIGHT_KG,
} from '@/lib/constants';
import { EmissionsCargoItemInterface, EmissionsFormInputsInterface, EmissionsSegmentInterface } from '@/types';

export const EMISSIONS_CALC_FORM_FIELDS = {
  MODE: 'mode',
  CARGO_ITEMS: 'cargoItems',
  CARGO_TYPE: 'type',
  QUANTITY: 'quantity',
  CONTAINER_SIZE: 'containerSize',
  TOTAL_WEIGHT: 'totalWeight',
  IS_REFRIGERATED: 'isRefrigerated',
  WEIGHT_UNIT: 'weightUnit',
  SEGMENTS: 'segments',
  VEHICLE_TYPE: 'vehicleType',
  VEHICLE_ID: 'vehicleId',
  DISTANCE: 'distance',
  DISTANCE_UNIT: 'distanceUnit',
  FLIGHT_NUMBER: 'flightNumber',
} as const;

export const EMISSIONS_CALC_INTERMODAL_DEFAULT_CARGO_ITEMS: EmissionsCargoItemInterface[] = [
  {
    [EMISSIONS_CALC_FORM_FIELDS.CARGO_TYPE]: CARGO_TYPES.CONTAINER,
    [EMISSIONS_CALC_FORM_FIELDS.QUANTITY]: 1,
    [EMISSIONS_CALC_FORM_FIELDS.CONTAINER_SIZE]: CONTAINER_SIZE.TWENTY,
    [EMISSIONS_CALC_FORM_FIELDS.TOTAL_WEIGHT]: 10000,
    [EMISSIONS_CALC_FORM_FIELDS.IS_REFRIGERATED]: false,
    weightUnit: UNIT_WEIGHT_KG,
  },
];

export const EMISSIONS_CALC_AIR_DEFAULT_CARGO_ITEMS: EmissionsCargoItemInterface[] = [
  {
    [EMISSIONS_CALC_FORM_FIELDS.CARGO_TYPE]: CARGO_TYPES.ITEM,
    [EMISSIONS_CALC_FORM_FIELDS.QUANTITY]: 1,
    [EMISSIONS_CALC_FORM_FIELDS.TOTAL_WEIGHT]: 100,
    weightUnit: UNIT_WEIGHT_KG,
  },
];

export const EMISSIONS_CALC_FORM_SEGMENT_DEFAULTS: EmissionsSegmentInterface = {
  vehicle: {
    type: null,
    vehicleId: '',
  },
  distance: 0,
  distanceUnit: DISTANCE_UNITS.KM,
  origin: undefined,
  stopOffs: [],
};

export const EMISSIONS_CALC_FORM_FIELD_DEFAULTS: EmissionsFormInputsInterface = {
  mode: MODE_INTERMODAL,
  cargoItems: EMISSIONS_CALC_INTERMODAL_DEFAULT_CARGO_ITEMS,
  segments: [EMISSIONS_CALC_FORM_SEGMENT_DEFAULTS, EMISSIONS_CALC_FORM_SEGMENT_DEFAULTS],
};

// Example data for the emissions calculator demonstration
export const EMISSIONS_CALC_EXAMPLE_INTERMODAL: EmissionsFormInputsInterface = {
  ...EMISSIONS_CALC_FORM_FIELD_DEFAULTS,
  segments: [
    {
      vehicle: {
        type: 'truck',
        vehicleId: '',
      },
      distance: 0,
      distanceUnit: 'km',
      stopOffs: [],
      origin: {
        type: 'city',
        name: 'Shenzhen, Guangdong, China',
        placeId: 'worldCityId 1156158707|canonical shenzhen-cn',
        location: {
          lat: 22.5415,
          lng: 114.0596,
        },
      },
    },
    {
      vehicle: {
        type: 'vessel',
        vehicleId: '9932907',
      },
      distance: 0,
      distanceUnit: 'km',
      stopOffs: [
        {
          type: 'seaport',
          name: 'Ningbo (CNNGB)',
          placeId: 'locode CNNBO|locode CNNGB',
        },
        {
          type: 'seaport',
          name: 'Shanghai (CNSHG)',
          placeId: 'locode CNSGH|locode CNSHA|locode CNSHG',
        },
      ],
      origin: {
        type: 'seaport',
        name: 'Yantian (CNYTN)',
        placeId: 'locode CNYTN',
        identifier: {
          type: 'locode',
          value: 'CNYTN',
        },
      },
    },
    {
      vehicle: {
        type: 'vessel',
        vehicleId: '9300465',
      },
      distance: 0,
      distanceUnit: 'km',
      stopOffs: [],
      origin: {
        type: 'seaport',
        name: 'Busan (KRPUS)',
        placeId: 'locode KRPUS',
        identifier: {
          type: 'locode',
          value: 'KRPUS',
        },
      },
    },
    {
      vehicle: {
        type: 'truck',
        vehicleId: '',
      },
      distance: 0,
      distanceUnit: 'km',
      stopOffs: [],
      origin: {
        type: 'seaport',
        name: 'Houston (USHOU)',
        placeId: 'locode USHOU',
        identifier: {
          type: 'locode',
          value: 'USHOU',
        },
      },
    },
    {
      vehicle: {
        type: null,
        vehicleId: '',
      },
      distance: 0,
      distanceUnit: 'km',
      stopOffs: [],
      origin: {
        type: 'city',
        name: 'Dallas, Texas, USA',
        placeId: 'worldCityId 1840019440|canonical dallas-us',
        location: {
          lat: 32.7935,
          lng: -96.7667,
        },
        identifier: {
          type: 'canonical',
          value: 'dallas-us',
        },
      },
    },
    {
      vehicle: {
        type: null,
        vehicleId: '',
      },
      distance: 0,
      distanceUnit: 'km',
      stopOffs: [],
    },
  ],
};

export const EMISSIONS_CALC_EXAMPLE_AIR: EmissionsFormInputsInterface = {
  mode: MODE_AIR,
  cargoItems: [
    {
      ...EMISSIONS_CALC_AIR_DEFAULT_CARGO_ITEMS[0],
      [EMISSIONS_CALC_FORM_FIELDS.TOTAL_WEIGHT]: 520,
    },
  ],
  segments: [
    {
      vehicle: {
        type: 'truck',
        vehicleId: '',
      },
      distance: 0,
      distanceUnit: 'km',
      stopOffs: [],
      origin: {
        type: 'city',
        name: 'Antwerp, Flanders, Belgium',
        placeId: 'worldCityId 1056168623|canonical antwerp-be',
        location: {
          lat: 51.2178,
          lng: 4.4003,
        },
        identifier: {
          type: 'canonical',
          value: 'antwerp-be',
        },
      },
    },
    {
      vehicle: {
        type: 'aircraft',
        vehicleId: '74X',
      },
      distance: 0,
      distanceUnit: 'km',
      stopOffs: [],
      origin: {
        type: 'airport',
        name: 'Amsterdam Airport Schiphol (AMS)',
        placeId: 'iata AMS|icao EHAM',
        identifier: {
          type: 'iata',
          value: 'AMS',
        },
      },
    },
    {
      vehicle: {
        type: 'aircraft',
        vehicleId: '332',
      },
      distance: 0,
      distanceUnit: 'km',
      stopOffs: [],
      origin: {
        type: 'airport',
        name: 'Newark Liberty International Airport (EWR)',
        placeId: 'iata EWR|icao KEWR',
        identifier: {
          type: 'iata',
          value: 'EWR',
        },
      },
    },
    {
      vehicle: {
        type: 'truck',
        vehicleId: '',
      },
      distance: 0,
      distanceUnit: 'km',
      stopOffs: [],
      origin: {
        type: 'airport',
        name: "Chicago O'Hare International Airport (ORD)",
        placeId: 'iata ORD|icao KORD',
        identifier: {
          type: 'iata',
          value: 'ORD',
        },
      },
    },
    {
      vehicle: {
        type: null,
        vehicleId: '',
      },
      distance: 0,
      distanceUnit: 'km',
      stopOffs: [],
      origin: {
        type: 'city',
        name: 'Chicago, Illinois, USA',
        placeId: 'worldCityId 1840000494|canonical chicago-us',
        location: {
          lat: 41.8375,
          lng: -87.6866,
        },
        identifier: {
          type: 'canonical',
          value: 'chicago-us',
        },
      },
    },
    {
      vehicle: {
        type: null,
        vehicleId: '',
      },
      distance: 0,
      distanceUnit: 'km',
      stopOffs: [],
    },
  ],
};

export const EMISSIONS_REPORT_STATUSES = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
} as const;
